import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { SmallIconButton } from '~pages/Approvals/Files/components/TicketDrawer/styled';

interface DrawerActionsProps {
  canDelete: boolean;
  canEdit: boolean;
  isBusy: boolean;
  isDirty: boolean;
  isEditing: boolean;
  isSaving: boolean;
  onCancelEdit: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onSave: () => void;
  sx?: SxProps<Theme>;
}

export default function DrawerActions({
  canDelete,
  canEdit,
  isBusy,
  isDirty,
  isEditing,
  isSaving,
  onCancelEdit,
  onDelete,
  onEdit,
  onSave,
  sx,
}: DrawerActionsProps) {
  const barSx: SxProps<Theme> = [
    { alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'flex-end' },
    ...(Array.isArray(sx) ? sx : [sx]),
  ];
  const outlinedButtonSx = (theme: Theme) => ({
    bgcolor: 'background.paper',
    border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
  });

  // Distinct keys remount the bar on the read/edit switch, so React can't reuse the
  // green Save node as Edit and flash its background through the transition.
  if (isEditing) {
    return (
      <Box key="edit-actions" sx={barSx}>
        <SmallButton
          color="inherit"
          disabled={isSaving}
          onClick={onCancelEdit}
          sx={outlinedButtonSx}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>
        <SmallLoadingButton
          color="brandV2Green"
          disabled={!isDirty}
          loading={isSaving}
          onClick={onSave}
        >
          {t('actions.save_changes')}
        </SmallLoadingButton>
      </Box>
    );
  }

  if (!canDelete && !canEdit) {
    return null;
  }

  return (
    <Box key="read-actions" sx={barSx}>
      {canDelete && (
        <Tooltip describeChild title={t('approvals.timesheets.delete_timesheet')}>
          <span>
            <SmallIconButton
              aria-label={t('actions.delete')}
              disabled={isBusy}
              onClick={onDelete}
            >
              <DeleteOutlineOutlined
                sx={(theme) => ({ color: theme.brandV2.colors.treadRed })}
              />
            </SmallIconButton>
          </span>
        </Tooltip>
      )}
      {canEdit && (
        <SmallButton
          color="inherit"
          disabled={isBusy}
          onClick={onEdit}
          startIcon={<Edit />}
          sx={outlinedButtonSx}
          variant="outlined"
        >
          {t('actions.edit')}
        </SmallButton>
      )}
    </Box>
  );
}
